import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link } from 'react-router-dom';
import {
  Menu, X, ChevronDown, Sun, Moon, Globe,
  Building2, Home as HomeIcon, Stethoscope, Cpu, Database, HeadphonesIcon, Briefcase,
  BookOpen, Video, FileText, GraduationCap, Check
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import SiteSearch from '@/components/marketing/SiteSearch';
import UserMenu, { MobileUserLinks } from '@/components/marketing/UserMenu';
import { trackDemoClick } from '@/lib/analytics';

const productItems = [
  { label: 'Domiciliary Care', icon: HomeIcon, href: '/domiciliary-care' },
  { label: 'Care Home Management', icon: Building2, href: '/care-home-management' },
  { label: 'Nursing Home Management', icon: Stethoscope, href: '/nursing-home-management' },
  { label: 'Enterprise Solutions', icon: Briefcase, href: '/enterprise-solutions' },
  { label: 'AI Features', icon: Cpu, href: '/ai-features' },
  { label: 'Data Migration', icon: Database, href: '/data-migration' },
  { label: 'Customer Support', icon: HeadphonesIcon, href: '/customer-support' },
];

const trainingItems = [
  { label: 'Video Tutorials', icon: Video, href: '/training#videos' },
  { label: 'Documentation', icon: FileText, href: '/training#docs' },
  { label: 'Knowledge Base', icon: BookOpen, href: '/training#kb' },
  { label: 'Certification Courses', icon: GraduationCap, href: '/training#courses' },
];

const aboutItems = [
  { label: 'About Us', href: '/about' },
  { label: 'Careers', href: '/careers' },
  { label: 'Blog & Resources', href: '/blog' },
  { label: 'Vendor / Partner Program', href: '/partner-program' },
  { label: 'Case Studies', href: '/case-studies' },
];

const THEMES = [
  { id: 'light', label: 'Light', color: '#f8fafc' },
  { id: 'dark', label: 'Dark', color: '#0f172a' },
  { id: 'ocean', label: 'Ocean', color: '#0ea5e9' },
  { id: 'forest', label: 'Forest', color: '#10b981' },
  { id: 'warm', label: 'Warm', color: '#f59e0b' },
];

const COUNTRIES = [
  { code: 'GB', flag: '🇬🇧', label: 'United Kingdom', active: true },
  { code: 'AU', flag: '🇦🇺', label: 'Australia', active: false },
  { code: 'NZ', flag: '🇳🇿', label: 'New Zealand', active: false },
];

export default function Header({ theme, setTheme, solid = false }) {
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [countryOpen, setCountryOpen] = useState(false);
  const [selectedCountry, setSelectedCountry] = useState(COUNTRIES[0]);
  const countryRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    const handleClick = (e) => {
      if (countryRef.current && !countryRef.current.contains(e.target)) setCountryOpen(false);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);

  const isDark = theme === 'dark';

  const navLinks = [
    { label: 'Home', href: '/' },
    { label: 'Products', href: '/products', dropdown: productItems },
    { label: 'Pricing', href: '/pricing' },
    { label: 'Training', href: '/training', dropdown: trainingItems },
    { label: 'About Us', href: '/about', dropdown: aboutItems },
    { label: 'Contact', href: '/contact' },
  ];

  return (
    <header className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
      scrolled || solid
        ? 'bg-background/90 backdrop-blur-xl shadow-lg shadow-primary/5 border-b border-border/50'
        : 'bg-transparent'
    }`}>
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-16">
        <div className="flex items-center justify-between h-20 lg:h-24">

          {/* Logo */}
          <Link to="/" className="flex items-center gap-2.5 group flex-shrink-0">
            <img
              src="/brand/pch-logo.svg"
              alt="People Care Home Logo"
              className="h-12 lg:h-14 w-auto object-contain dark:invert"
            />
          </Link>

          {/* Desktop Nav */}
          <nav className="hidden lg:flex items-center gap-0.5">
            {navLinks.map((link) => (
              <div
                key={link.label}
                className="relative"
                onMouseEnter={() => link.dropdown && setActiveDropdown(link.label)}
                onMouseLeave={() => setActiveDropdown(null)}
              >
                <Link
                  to={link.href}
                  className="flex items-center gap-1 px-3 py-2 text-sm font-medium text-muted-foreground hover:text-foreground transition-colors rounded-lg hover:bg-muted/50"
                >
                  {link.label}
                  {link.dropdown && <ChevronDown className="w-3.5 h-3.5 mt-0.5" />}
                </Link>

                <AnimatePresence>
                  {link.dropdown && activeDropdown === link.label && (
                    <motion.div
                      initial={{ opacity: 0, y: 8, scale: 0.96 }}
                      animate={{ opacity: 1, y: 0, scale: 1 }}
                      exit={{ opacity: 0, y: 8, scale: 0.96 }}
                      transition={{ duration: 0.15 }}
                      className="absolute top-full left-0 mt-1 w-60 bg-card/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-2xl shadow-primary/10 p-2"
                    >
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-muted/70 transition-colors group"
                        >
                          {item.icon && <item.icon className="w-4 h-4 text-primary group-hover:text-accent transition-colors" />}
                          <span className="text-sm font-medium text-foreground">{item.label}</span>
                        </Link>
                      ))}
                    </motion.div>
                  )}
                </AnimatePresence>
              </div>
            ))}
          </nav>

          {/* Right Side */}
          <div className="flex items-center gap-1.5">

            {/* Site Search */}
            <SiteSearch />

            {/* Dark/Light quick toggle */}
            <button
              onClick={() => setTheme(isDark ? 'light' : 'dark')}
              className="p-2 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all"
              aria-label="Toggle dark/light"
            >
              {isDark ? <Sun className="w-4 h-4" /> : <Moon className="w-4 h-4" />}
            </button>

            {/* Country Selector */}
            <div ref={countryRef} className="relative hidden sm:block">
              <button
                onClick={() => { setCountryOpen(!countryOpen); }}
                className="flex items-center gap-1.5 px-2 py-1.5 rounded-lg hover:bg-muted/50 text-muted-foreground hover:text-foreground transition-all text-xs"
              >
                <span>{selectedCountry.flag}</span>
                <span className="hidden md:block">{selectedCountry.code}</span>
                <ChevronDown className="w-3 h-3" />
              </button>
              <AnimatePresence>
                {countryOpen && (
                  <motion.div
                    initial={{ opacity: 0, y: 8, scale: 0.96 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    exit={{ opacity: 0, y: 8, scale: 0.96 }}
                    transition={{ duration: 0.15 }}
                    className="absolute top-full right-0 mt-1 w-52 bg-card/95 backdrop-blur-xl border border-border/50 rounded-xl shadow-2xl p-2"
                  >
                    <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wider px-2 pb-1">Region</p>
                    {COUNTRIES.map((c) => (
                      <button
                        key={c.code}
                        disabled={!c.active}
                        onClick={() => { if (c.active) { setSelectedCountry(c); setCountryOpen(false); } }}
                        className={`w-full flex items-center gap-2.5 px-2 py-2 rounded-lg text-sm transition-colors text-left ${
                          c.active
                            ? 'hover:bg-muted/60 text-foreground cursor-pointer'
                            : 'text-muted-foreground/40 cursor-not-allowed'
                        }`}
                      >
                        <span>{c.flag}</span>
                        <span className="flex-1">{c.label}</span>
                        {!c.active && <span className="text-xs text-muted-foreground/40">Soon</span>}
                        {selectedCountry.code === c.code && <Check className="w-3.5 h-3.5 text-primary" />}
                      </button>
                    ))}
                  </motion.div>
                )}
              </AnimatePresence>
            </div>

            <div className="hidden md:flex items-center gap-2">
              <Link to="/pricing">
                <Button variant="ghost" size="sm" className="text-sm font-medium">
                  Free Trial
                </Button>
              </Link>
              <Link to="/contact">
                <Button size="sm" onClick={() => trackDemoClick('header')} className="text-sm font-medium bg-gradient-to-r from-primary to-primary/80 hover:from-primary/90 hover:to-primary/70 shadow-lg shadow-primary/25">
                  Get Demo
                </Button>
              </Link>
              <UserMenu />
            </div>

            <button
              onClick={() => setMobileOpen(!mobileOpen)}
              className="lg:hidden p-2 rounded-lg hover:bg-muted/50 text-foreground"
            >
              {mobileOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
            </button>
          </div>
        </div>
      </div>

      {/* Mobile Menu */}
      <AnimatePresence>
        {mobileOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="lg:hidden bg-card/95 backdrop-blur-xl border-t border-border/50 overflow-hidden"
            style={{ maxHeight: 'calc(100vh - 80px)', overflowY: 'auto' }}
          >
            <div className="max-w-screen-2xl mx-auto px-4 sm:px-8 lg:px-16 py-4 space-y-1">
              {navLinks.map((link) => (
                <div key={link.label}>
                  <Link
                    to={link.href}
                    onClick={() => setMobileOpen(false)}
                    className="block px-4 py-2.5 text-sm font-medium text-foreground rounded-lg hover:bg-muted/50 transition-colors"
                  >
                    {link.label}
                  </Link>
                  {link.dropdown && (
                    <div className="pl-6 space-y-0.5">
                      {link.dropdown.map((item) => (
                        <Link
                          key={item.label}
                          to={item.href}
                          onClick={() => setMobileOpen(false)}
                          className="block px-4 py-2 text-sm text-muted-foreground hover:text-foreground rounded-lg hover:bg-muted/30 transition-colors"
                        >
                          {item.label}
                        </Link>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              <MobileUserLinks onNavigate={() => setMobileOpen(false)} />
              <div className="flex gap-2 pt-3 px-4">
                <Link to="/pricing" className="flex-1" onClick={() => setMobileOpen(false)}>
                  <Button variant="outline" className="w-full" size="sm">Free Trial</Button>
                </Link>
                <Link to="/contact" className="flex-1" onClick={() => setMobileOpen(false)}>
                  <Button className="w-full" size="sm">Get Demo</Button>
                </Link>
              </div>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
}